import { getPlatform, getOsVersion } from '../components/Helpers'

// Phase 1 analytics ingestion. Events are buffered in memory and flushed in
// batches to the public events endpoint. Keep all event-building centralized
// here so Phase 2 field additions/renames stay in one place.

const API_URL = process.env.REACT_APP_API_URL
const BATCH_URL = `${API_URL}/events/batch`

const PLATFORM = 'web_viewer'

// Max events per batch request (server rejects >100).
const MAX_BATCH = 100
// Flush once the buffer reaches this many events.
const FLUSH_THRESHOLD = 20
// Periodic flush cadence so low-frequency events don't linger.
const FLUSH_INTERVAL_MS = 8000
// How long a target can be out of frame before it counts as a real drop-off.
const GRACE_MS = 1500

function makeUuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }
    // Fallback for non-secure contexts / older browsers.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}

// One session id per viewer load, reused for every event. Spans multiple scans.
const SESSION_ID = makeUuid()

// Anonymous device id, generated once and reused forever via localStorage.
function getAnonymizedId() {
    try {
        let id = localStorage.getItem('pxl_anon_id')
        if (!id) {
            id = makeUuid()
            localStorage.setItem('pxl_anon_id', id)
        }
        return id
    } catch (_) {
        // Storage blocked (private mode / disabled). Fall back to the session id
        // so events still carry a stable-per-session anonymized id.
        return SESSION_ID
    }
}

const buffer = []
let flushTimer = null

function scheduleFlush() {
    if (flushTimer !== null) return
    flushTimer = setTimeout(() => {
        flushTimer = null
        flush()
    }, FLUSH_INTERVAL_MS)
}

function chunk(arr, size) {
    const out = []
    for (let i = 0; i < arr.length; i += size) {
        out.push(arr.slice(i, i + size))
    }
    return out
}

export function flush({ useBeacon = false } = {}) {
    if (buffer.length === 0) return

    if (flushTimer !== null) {
        clearTimeout(flushTimer)
        flushTimer = null
    }

    const batches = chunk(buffer.splice(0, buffer.length), MAX_BATCH)

    for (const events of batches) {
        const body = JSON.stringify({ events })

        if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
            const blob = new Blob([body], { type: 'application/json' })
            navigator.sendBeacon(BATCH_URL, blob)
            continue
        }

        fetch(BATCH_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
        }).catch((err) => {
            // Best-effort: analytics failures must never break the viewer.
            console.warn('[Analytics] flush failed:', err)
        })
    }
}

// Single source of truth for the event envelope. `metadata` from callers is
// merged onto the baseline device/os fields rather than replacing them.
function track(eventType, { projectId, occurredAt, metadata, ...fields } = {}) {
    if (!projectId) return

    buffer.push({
        project_id: projectId,
        event_type: eventType,
        session_id: SESSION_ID,
        anonymized_id: getAnonymizedId(),
        platform: PLATFORM,
        occurred_at: (occurredAt || new Date()).toISOString(),
        metadata: { device: getPlatform(), os_version: getOsVersion(), ...(metadata || {}) },
        ...fields,
    })

    if (buffer.length >= FLUSH_THRESHOLD) {
        flush()
    } else {
        scheduleFlush()
    }
}

export function trackImageTargetScanned(projectId, occurredAt) {
    track('image_target_scanned', { projectId, occurredAt })
}

export function trackArExperienceStarted(projectId, occurredAt) {
    track('ar_experience_started', { projectId, occurredAt })
}

export function trackArExperienceProgress(
    projectId,
    { watchedDuration, totalDuration, completedFlag, dropOffTime },
    occurredAt
) {
    track('ar_experience_progress', {
        projectId,
        occurredAt,
        watched_duration: Math.max(0, Math.round(watchedDuration || 0)),
        total_duration: Math.max(0, Math.round(totalDuration || 0)),
        completed_flag: !!completedFlag,
        drop_off_time: Math.max(0, Math.round(dropOffTime || 0)),
    })
}

export function trackCtaPressed(projectId, ctaUrl) {
    track('cta_pressed', { projectId, cta_url: ctaUrl })
}

// One-shot project-load timing report. Fires once when the experience becomes
// ready to use. Timings ride the free-form metadata object as integer ms.
export function trackDiagnostic(
    projectId,
    { totalLoadTimeMs, previewFetchTimeMs, assetDownloadTimeMs } = {},
    occurredAt
) {
    track('diagnostic', {
        projectId,
        occurredAt,
        metadata: {
            total_load_time_ms: Math.max(0, Math.round(totalLoadTimeMs || 0)),
            preview_fetch_time_ms: Math.max(0, Math.round(previewFetchTimeMs || 0)),
            asset_download_time_ms: Math.max(0, Math.round(assetDownloadTimeMs || 0)),
        },
    })
}

// --- Per-target view tracking with grace window -----------------------------
//
// A "view" spans from the first time a target plays until it is genuinely lost.
// Brief look-aways (out of frame for < GRACE_MS) are tolerated and continue the
// same view rather than producing a drop-off.

const views = new Map() // key: `${projectId}-${idx}` -> view state

function viewKey(projectId, idx) {
    return `${projectId}-${idx}`
}

function attachCompletionWatcher(view, videoEl) {
    if (!videoEl || view.onTimeUpdate) return
    let prevTime = videoEl.currentTime || 0
    view.onTimeUpdate = () => {
        const current = videoEl.currentTime || 0
        const duration = videoEl.duration || 0
        // Loop wrap (rewound) or reached the end => watched through at least once.
        if ((prevTime > current + 0.5) || (duration > 0 && current >= duration - 0.3)) {
            view.completed = true
        }
        prevTime = current
    }
    videoEl.addEventListener('timeupdate', view.onTimeUpdate)
}

function detachCompletionWatcher(view) {
    if (view.videoEl && view.onTimeUpdate) {
        view.videoEl.removeEventListener('timeupdate', view.onTimeUpdate)
    }
    view.onTimeUpdate = null
}

function accumulateWatched(view, now) {
    if (view.playing && view.playStart != null) {
        view.watchedMs += now - view.playStart
        view.playStart = null
    }
    view.playing = false
}

function finalizeView(key, view, occurredAt) {
    if (view.graceTimer) {
        clearTimeout(view.graceTimer)
        view.graceTimer = null
    }
    detachCompletionWatcher(view)

    const totalDuration = view.videoEl && view.videoEl.duration
        ? view.videoEl.duration * 1000
        : 0

    trackArExperienceProgress(
        view.projectId,
        {
            watchedDuration: view.watchedMs,
            totalDuration,
            completedFlag: view.completed,
            dropOffTime: view.dropOffMs,
        },
        occurredAt
    )

    views.delete(key)
}

// Called when a target starts playing (active scanning). Starts a new view, or
// resumes the current one if we're inside the grace window.
export function onTargetView(projectId, idx, videoEl) {
    if (projectId == null || idx == null) return
    const key = viewKey(projectId, idx)
    const existing = views.get(key)
    const now = Date.now()

    if (existing) {
        // Re-acquired within grace: cancel the pending finalize and resume.
        if (existing.graceTimer) {
            clearTimeout(existing.graceTimer)
            existing.graceTimer = null
        }
        existing.videoEl = videoEl || existing.videoEl
        existing.playing = true
        existing.playStart = now
        attachCompletionWatcher(existing, existing.videoEl)
        return
    }

    const view = {
        projectId,
        idx,
        videoEl,
        playing: true,
        playStart: now,
        watchedMs: 0,
        dropOffMs: videoEl ? (videoEl.currentTime || 0) * 1000 : 0,
        completed: false,
        graceTimer: null,
        onTimeUpdate: null,
    }
    views.set(key, view)
    attachCompletionWatcher(view, videoEl)

    trackImageTargetScanned(projectId)
    trackArExperienceStarted(projectId)
}

// Called when a target is lost. Starts the grace timer; if no re-acquisition
// happens before it fires, the view is finalized as a drop-off.
export function onTargetLost(projectId, idx, videoEl) {
    if (projectId == null || idx == null) return
    const key = viewKey(projectId, idx)
    const view = views.get(key)
    if (!view) return

    const now = Date.now()
    accumulateWatched(view, now)
    if (videoEl) {
        view.videoEl = videoEl
        view.dropOffMs = (videoEl.currentTime || 0) * 1000
    }

    const lostAt = new Date(now)
    if (view.graceTimer) clearTimeout(view.graceTimer)
    view.graceTimer = setTimeout(() => {
        finalizeView(key, view, lostAt)
    }, GRACE_MS)
}

// Finalize every active view immediately (teardown / unload).
export function endActiveViews(occurredAt) {
    const now = Date.now()
    for (const [key, view] of views) {
        accumulateWatched(view, now)
        finalizeView(key, view, occurredAt)
    }
}

// Lifecycle: flush (and finalize) when the page is hidden or unloaded.
if (typeof window !== 'undefined') {
    const onHide = () => {
        endActiveViews()
        flush({ useBeacon: true })
    }
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') onHide()
    })
    window.addEventListener('pagehide', onHide)
}
